import React, { useMemo } from "react";
import { Box, FormControl, FormHelperText, FormLabel } from "@mui/material";
import MuiTextField from "@mui/material/TextField";
import { FIELD_SX } from "./fieldProps";
import { ADDRESS_LABELS, addressMissing, addressParts, countryOptions } from "../model/address";

const AUTOCOMPLETE = { line1: "address-line1", line2: "address-line2", city: "address-level2", region: "address-level1", postcode: "postal-code" };

/**
 * `address` — a country picker, then that country's sub-fields (`model/address.js`), all submitted
 * as ONE structured value. Each part is its own labelled input with its autofill token.
 */
export default function AddressField({ id, field, value, error, onChange, onBlur, msg, lang }) {
	const v = value && typeof value === "object" ? value : {};
	const countries = useMemo(() => countryOptions(lang), [lang]);
	const missing = error ? addressMissing(v) : [];
	const helpId = error || field.hint ? `${id}-help` : undefined;
	const label = (key) => msg(`widget.address_${ADDRESS_LABELS[key][0]}`, ADDRESS_LABELS[key][1]);
	const set = (key) => (e) => onChange({ ...v, [key]: e.target.value });
	const common = { fullWidth: true, size: "small", sx: FIELD_SX };

	return (
		<FormControl component="fieldset" error={!!error} required={field.required === true} fullWidth onBlur={onBlur} aria-describedby={helpId}>
			<FormLabel component="legend" sx={{ fontSize: 13, fontWeight: 500, color: "var(--w-text)", mb: 1, "&.Mui-focused, &.Mui-error": { color: "var(--w-text)" } }}>
				{field.label}
			</FormLabel>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
				<MuiTextField {...common} id={`${id}-country`} select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
					label={label("country")} required value={v.country || ""} onChange={set("country")} error={missing.includes("country")}
					inputProps={{ autoComplete: "country" }}>
					<option value="">{msg("widget.select_placeholder", "Choose…")}</option>
					{countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
				</MuiTextField>
				{addressParts(v.country).map((p) => (
					<MuiTextField {...common} key={p.key} id={`${id}-${p.key}`} label={label(p.label)} required={p.required}
						value={v[p.key] || ""} onChange={set(p.key)} error={missing.includes(p.key)}
						inputProps={{ autoComplete: AUTOCOMPLETE[p.key] }} />
				))}
			</Box>
			{(error || field.hint) && <FormHelperText id={helpId} sx={{ mx: 0, color: error ? "var(--w-error)" : "var(--w-text)" }}>{error || field.hint}</FormHelperText>}
		</FormControl>
	);
}
